import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// In-memory fallback for Safari Private Browsing / storage issues
let memoryStorage = {};

// Safe localStorage wrapper for Safari/iOS compatibility
const safeStorage = {
  isAvailable: () => {
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      console.warn('localStorage not available, using memory fallback');
      return false;
    }
  },
  
  getItem: (key) => {
    try {
      if (safeStorage.isAvailable()) {
        return localStorage.getItem(key);
      }
      return memoryStorage[key] || null;
    } catch (e) {
      return memoryStorage[key] || null;
    }
  },
  
  setItem: (key, value) => {
    try {
      if (safeStorage.isAvailable()) {
        localStorage.setItem(key, value);
      }
      memoryStorage[key] = value;
    } catch (e) {
      memoryStorage[key] = value;
    }
  },
  
  removeItem: (key) => {
    try {
      if (safeStorage.isAvailable()) {
        localStorage.removeItem(key);
      }
      delete memoryStorage[key];
    } catch (e) {
      delete memoryStorage[key];
    }
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Normalise user object to support BOTH backend response shapes:
  //  - Legacy:  { is_pro, pro_plan, pro_expires }
  //  - New IAP: { subscription_status: 'active', subscription_expiry, product_id }
  const normaliseUser = (u) => {
    if (!u) return u;
    const isPro = u.is_pro === true || u.subscription_status === 'active';
    const expires = u.pro_expires || u.subscription_expiry || null;
    const plan = u.pro_plan || u.product_id || null;
    return {
      ...u,
      is_pro: isPro,
      pro_plan: plan,
      pro_expires: expires,
      subscription_status: u.subscription_status || (isPro ? 'active' : 'free'),
      subscription_expiry: expires,
    };
  };

  useEffect(() => {
    const token = safeStorage.getItem('autopostr_token');
    if (token) {
      fetchUser(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const text = await response.text();
      if (response.ok && text) {
        try {
          const userData = JSON.parse(text);
          setUser(normaliseUser(userData));
        } catch (e) {
          safeStorage.removeItem('autopostr_token');
        }
      } else {
        safeStorage.removeItem('autopostr_token');
      }
    } catch (error) {
      console.error('Auth error:', error);
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    
    const text = await response.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch (e) {
      throw new Error(text || 'Login failed');
    }
    
    if (!response.ok) {
      throw new Error(data.detail || 'Login failed');
    }
    
    safeStorage.setItem('autopostr_token', data.token);
    setUser(normaliseUser(data.user));
    setShowAuthModal(false);
    // Auto-redirect to dashboard on successful login (unless already there)
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/dashboard')) {
      window.location.href = '/dashboard';
    }
    return data;
  };

  const register = async (email, password, name) => {
    const response = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name })
    });
    
    const text = await response.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch (e) {
      throw new Error(text || 'Registration failed');
    }
    
    if (!response.ok) {
      throw new Error(data.detail || 'Registration failed');
    }
    
    safeStorage.setItem('autopostr_token', data.token);
    setUser(normaliseUser(data.user));
    setShowAuthModal(false);
    // Auto-redirect new users to dashboard after successful registration
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/dashboard')) {
      window.location.href = '/dashboard';
    }
    return data;
  };

  const logout = () => {
    safeStorage.removeItem('autopostr_token');
    setUser(null);
  };

  const refreshUser = async () => {
    const token = safeStorage.getItem('autopostr_token');
    if (token) {
      await fetchUser(token);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      logout,
      refreshUser,
      getAuthHeaders: () => {
        const token = safeStorage.getItem('autopostr_token');
        return token ? { Authorization: `Bearer ${token}` } : {};
      },
      showAuthModal,
      setShowAuthModal,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};
